/**
 * Packages - the price sheet, as a page.
 *
 * v4.2, EUR, tax-exclusive, rendered from `config/pricing` so it draws
 * instantly and correctly with no catalogue call to wait on. That matters for
 * exactly one reason: this page gets shown to a customer across a table, and a
 * pricing page that spins is a pricing page you cannot use.
 *
 * Three bands, in the order a buyer decides in: the seat, the modules that go
 * on the seat, and the packs that top either of them up. Packs are given real
 * estate rather than a footnote - a €29 prepaid pack is the easiest yes on
 * this page and the one thing here that can be bought in two clicks.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { PiCheckLight, PiInfinityLight } from 'react-icons/pi';
import { RootState } from '../../../store';
import {
  ALLOWANCE_RULES,
  BillingPeriod,
  ENTERPRISE_BANDS,
  MODULES,
  PACKS,
  PLANS,
  REGIONAL_TIERS,
  annualSavingPercent,
  describeAllowance,
  formatEur,
  seatPriceAtTier,
} from '../../../config/pricing';
import GlassCard from '../brand/GlassCard';
import Button from '../TailwindComponents/Button';
import Badge from '../TailwindComponents/Badge';

const PeriodToggle: React.FC<{
  period: BillingPeriod;
  onChange: (period: BillingPeriod) => void;
}> = ({ period, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-full bg-surface-sunken
                    border border-hairline-soft" role="radiogroup"
         data-testid="pricing-period">
      {(['annual', 'monthly'] as BillingPeriod[]).map((option) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={period === option}
          onClick={() => onChange(option)}
          data-testid={`pricing-period-${option}`}
          className={
            period === option
              ? 'px-3 py-1 rounded-full text-xs font-medium bg-champagne/20 text-fg-strong'
              : 'px-3 py-1 rounded-full text-xs text-fg-muted hover:text-fg-strong transition-colors duration-200'
          }
        >
          {option === 'annual' ? t('Annual') : t('Monthly')}
        </button>
      ))}
    </div>
  );
};

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [period, setPeriod] = useState<BillingPeriod>('annual');

  /**
   * The regional tier, when the org carries one. SEATS ONLY - a pack is €29
   * everywhere, so applying the discount to one would misquote a checkout the
   * backend is about to price itself.
   */
  const orgTier = useSelector(
    (s: RootState) => (s.user?.userInfo as any)?.organization?.regional_tier as string | undefined
  );
  const tier = orgTier && REGIONAL_TIERS[orgTier] ? REGIONAL_TIERS[orgTier] : null;

  const seatPrice = useMemo(
    () => (cents: number | null) =>
      cents === null ? null : seatPriceAtTier(cents, tier?.code),
    [tier]
  );

  const perSeat = t('per seat / month');

  return (
    <div className="h-full overflow-y-auto custom-scrollbar-main bg-bg">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">

        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-1.5">
            <h1 className="text-xl font-medium text-fg-strong">{t('Packages')}</h1>
            <p className="text-sm text-fg-muted">
              {t('All prices in EUR, excluding tax.')}
              {tier && tier.discount > 0 && (
                <span className="ms-1 text-champagne">
                  {t('Seat prices shown include your regional discount.')}
                </span>
              )}
            </p>
          </div>
          <PeriodToggle period={period} onChange={setPeriod} />
        </header>

        {/* ── Seats ─────────────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-fg-strong">{t('Plans')}</h2>
          <div className="grid gap-3 md:grid-cols-3">
            {PLANS.map((plan) => {
              const price = seatPrice(period === 'annual' ? plan.annual : plan.monthly);
              const saving = plan.annual && plan.monthly
                ? annualSavingPercent(plan.annual, plan.monthly) : 0;
              return (
                <GlassCard
                  key={plan.code}
                  className={plan.featured ? 'border-champagne/50' : undefined}
                  data-testid={`plan-${plan.code}`}
                >
                  <div className="flex flex-col h-full gap-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-fg-strong">{t(plan.name)}</p>
                      {plan.featured && <Badge color="yellow">{t('Most popular')}</Badge>}
                    </div>
                    <p className="text-xs text-fg-muted leading-relaxed">{t(plan.tagline)}</p>

                    <div className="flex items-baseline gap-1.5">
                      {price === null ? (
                        <span className="text-2xl font-medium text-fg-strong">{t('Custom')}</span>
                      ) : (
                        <>
                          <span className="text-2xl font-medium text-fg-strong tabular-nums">
                            {formatEur(price)}
                          </span>
                          <span className="text-[11px] text-fg-muted">{perSeat}</span>
                        </>
                      )}
                    </div>
                    {period === 'annual' && saving > 0 && (
                      <p className="-mt-1.5 text-[11px] text-champagne">
                        {t('Save {{percent}}% paying annually', { percent: saving })}
                      </p>
                    )}

                    {/* The allowance IS the product on Full - a per-seat
                        monthly pool the whole firm shares. Saying "all modules
                        included" without it sells the licence and hides the
                        capacity. */}
                    {plan.allowance && (
                      <div className="rounded-lg bg-surface-sunken/70 border border-hairline-soft px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wide text-fg-muted">
                          {t('Per seat each month, pooled firm-wide')}
                        </p>
                        <p className="mt-0.5 text-xs text-fg-body leading-relaxed">
                          {describeAllowance(plan.allowance)}
                        </p>
                      </div>
                    )}

                    <ul className="space-y-1.5 pt-1">
                      {plan.includes.map((line) => (
                        <li key={line} className="flex items-start gap-2 text-xs text-fg-body">
                          <PiCheckLight className="w-3.5 h-3.5 mt-0.5 text-champagne flex-shrink-0" aria-hidden />
                          <span className="leading-relaxed">{t(line)}</span>
                        </li>
                      ))}
                    </ul>

                    {plan.footnote && (
                      <p className="text-[11px] text-fg-muted leading-relaxed">{t(plan.footnote)}</p>
                    )}

                    <div className="mt-auto pt-3">
                      <Button
                        variant={plan.featured ? 'primary' : 'outline'}
                        size="small"
                        className="w-full"
                        onClick={() => history.push('/plans')}
                        data-testid={`plan-cta-${plan.code}`}
                      >
                        {price === null ? t('Talk to us') : t('Choose {{plan}}', { plan: t(plan.name) })}
                      </Button>
                    </div>
                  </div>
                </GlassCard>
              );
            })}
          </div>
        </section>

        {/* ── Modules ───────────────────────────────────────────────── */}
        <section className="space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-medium text-fg-strong">{t('Modules')}</h2>
            <p className="text-[11px] text-fg-muted">
              {t('Added to a Starter seat. All of them are included in Full.')}
            </p>
          </div>
          <GlassCard padding="none">
            <ul className="divide-y divide-hairline-soft" data-testid="module-list">
              {MODULES.map((module) => (
                <li key={module.code} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm text-fg-strong">{t(module.name)}</p>
                    <p className="text-xs text-fg-muted">{t(module.description)}</p>
                    {/* What it adds to the pool. A module's allowance is the
                        difference between "you may use Tables" and "you may
                        run 500 extractions a month". */}
                    <p className="mt-0.5 text-[11px] text-champagne">
                      {module.allowance
                        ? describeAllowance(module.allowance) + t(' /seat/mo')
                        : t(module.allowanceNote || '')}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-end">
                    <p className="text-sm font-medium text-fg-strong tabular-nums">
                      {formatEur(seatPriceAtTier(
                        period === 'annual' ? module.annual : module.monthly, tier?.code
                      ))}
                    </p>
                    <p className="text-[10px] text-fg-muted">{perSeat}</p>
                  </div>
                </li>
              ))}
            </ul>
          </GlassCard>
        </section>

        {/* ── Packs - the easiest yes on the page ───────────────────── */}
        <section className="space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-medium text-fg-strong">{t('Flex packs')}</h2>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-fg-muted">
              <PiInfinityLight className="w-3.5 h-3.5 text-champagne" aria-hidden />
              {t('Prepaid, and they never expire')}
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="pricing-packs">
            {PACKS.map((pack) => (
              <GlassCard
                key={pack.code}
                className={pack.featured ? 'border-champagne/50' : undefined}
              >
                <div className="flex flex-col h-full gap-2">
                  <p className="text-xs text-fg-muted">{t(pack.name)}</p>
                  <p className="text-xl font-medium text-fg-strong tabular-nums leading-none">
                    {pack.units.toLocaleString()}
                  </p>
                  <p className="text-[11px] text-fg-muted">{t(pack.unitLabel)}</p>
                  <p className="mt-auto pt-2 text-sm font-medium text-fg-strong tabular-nums">
                    {formatEur(pack.price)}
                  </p>
                </div>
              </GlassCard>
            ))}
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={() => history.push('/packs')}
            data-testid="pricing-buy-packs"
          >
            {t('Buy a pack')}
          </Button>
        </section>

        {/* ── Enterprise, which is banded rather than quoted from nothing ── */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-fg-strong">{t('Enterprise')}</h2>
          <GlassCard padding="none">
            <ul className="divide-y divide-hairline-soft" data-testid="enterprise-bands">
              {ENTERPRISE_BANDS.map((band) => (
                <li key={band.seats} className="flex items-start justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm text-fg-strong">{t(band.seats)}</p>
                    <p className="text-xs text-fg-muted leading-relaxed">{t(band.includes)}</p>
                  </div>
                  <div className="flex-shrink-0 text-end">
                    <p className="text-sm font-medium text-fg-strong tabular-nums">
                      {formatEur(band.annual)}
                    </p>
                    <p className="text-[10px] text-fg-muted">{perSeat}</p>
                  </div>
                </li>
              ))}
            </ul>
          </GlassCard>
          <p className="text-[11px] text-fg-muted">
            {t('Annual invoice only. Volume bands discount from the regional price, not from Tier 1.')}
          </p>
        </section>

        {/* ── How the allowance behaves ─────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-fg-strong">{t('How the allowance works')}</h2>
          <GlassCard>
            <ul className="space-y-1.5">
              {ALLOWANCE_RULES.map((rule) => (
                <li key={rule} className="flex items-start gap-2 text-xs text-fg-body">
                  <PiCheckLight className="w-3.5 h-3.5 mt-0.5 text-champagne flex-shrink-0" aria-hidden />
                  <span className="leading-relaxed">{t(rule)}</span>
                </li>
              ))}
            </ul>
          </GlassCard>
        </section>

        {/* ── Regional pricing ──────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-fg-strong">{t('Regional pricing')}</h2>
          <GlassCard padding="none">
            <ul className="divide-y divide-hairline-soft" data-testid="regional-tiers">
              {Object.values(REGIONAL_TIERS).map((row) => (
                <li key={row.code}
                    className={clsx(
                      'flex items-start justify-between gap-4 px-4 py-3',
                      tier?.code === row.code && 'bg-champagne/10'
                    )}>
                  <div className="min-w-0">
                    <p className="text-sm text-fg-strong">
                      {t(row.label)}
                      {tier?.code === row.code && (
                        <span className="ms-2 text-[10px] text-champagne">{t('Your region')}</span>
                      )}
                    </p>
                    <p className="text-xs text-fg-muted leading-relaxed">{row.markets}</p>
                  </div>
                  <p className="flex-shrink-0 text-sm font-medium text-fg-strong tabular-nums">
                    {row.discount ? `−${Math.round(row.discount * 100)}%` : t('List')}
                  </p>
                </li>
              ))}
            </ul>
          </GlassCard>
          <p className="text-[11px] text-fg-muted">
            {t('The regional adjustment applies to seats and industry packs only. Flex and project packs are global list everywhere.')}
          </p>
        </section>

        <p className="text-[11px] text-fg-muted pt-2">
          {t('Prices are per seat per month unless stated otherwise, in EUR, excluding tax. Packs are one-off purchases.')}
        </p>
      </div>
    </div>
  );
};

export default PricingPage;
